// Apolo IDE — useEvaluator hook (E3 v3-batch-evaluator, WS2.5).
//
// Runs the batch evaluator (Worker aggregate + local LM Studio Gemma 4 E4B) to
// get rule proposals, applies an approved proposal to the .apolorules LEARNED
// block via the Electron writer + a `chore(learn): {rule-id}` commit, lists
// learned rules from the file, and reverts a rule (= git revert of its commit).

import { useCallback, useState } from 'react';
import { apiFetch } from '../lib/api.js';
import { syncApoloRulesToSession } from '../lib/sessionRulesSync.js';
import { runEvaluatorViaLmStudio, ensureEvaluatorScheduleTimezone } from '../lib/evaluatorLocal.js';
import { formatSelfImproveError } from '../lib/formatSelfImproveError.js';

const hasGit = () => typeof window !== 'undefined' && Boolean(window.apolo?.git);
const hasRules = () => typeof window !== 'undefined' && Boolean(window.apolo?.apolorules);

/** Trigger one batch evaluation via local LM Studio. Resolves { proposals, considered, error? }. */
export async function runEvaluatorRun(scope = 'all') {
  const res = await runEvaluatorViaLmStudio(scope);
  if (res.error) {
    return {
      proposals: [],
      considered: res.considered || 0,
      error: res.error,
    };
  }
  return res;
}

/**
 * Parse the LEARNED block of a .apolorules file into rule records (for the
 * history UI). Pure — round-trips the Electron writer's output.
 *
 * E4 — sets `disabled: true` on rules preceded by a "# DISABLED" comment line
 * in the file. Provenance fields (n, ext_pct, generated_at) are always present.
 *
 * @param {unknown} content
 * @returns {Array<{ rule_id: string, n: number, ext_pct: number, generated_at: string, rule: string, disabled?: boolean }>}
 */
export function parseLearnedFromContent(content) {
  if (typeof content !== 'string') return [];
  const start = content.indexOf('# === LEARNED');
  const end = content.indexOf('# === /LEARNED ===');
  if (start === -1 || end === -1 || end < start) return [];
  const region = content.slice(start, end);
  const out = [];
  let pending = null;
  let nextDisabled = false;
  for (const line of region.split(/\r?\n/)) {
    const meta = line.match(/#\s*rule-id:\s*(\S+)\s+evidence:\s*(\d+)\s+outcomes,\s*(\d+)%\s+ext-signal\s+generated:\s*(\S+)/);
    if (meta) {
      pending = { rule_id: meta[1], n: Number(meta[2]), ext_pct: Number(meta[3]), generated_at: meta[4] };
      continue;
    }
    // E4 — detect the # DISABLED marker written by formatLearnedBlock
    if (/^\s*#\s*DISABLED\s*$/.test(line)) {
      nextDisabled = true;
      continue;
    }
    const item = line.match(/^\s*-\s*"(.*)"\s*$/);
    if (item) {
      const record = {
        ...(pending || { rule_id: '', n: 0, ext_pct: 0, generated_at: '' }),
        rule: item[1].replace(/\\"/g, '"'),
      };
      if (nextDisabled) record.disabled = true;
      out.push(record);
      pending = null;
      nextDisabled = false;
    }
  }
  return out;
}

/** Load cron-stashed evaluator proposals (if any). */
export async function fetchEvaluatorPending() {
  try {
    const res = await apiFetch('/api/code-ide/evaluator/pending');
    if (!res.ok) return { pending: null };
    return { pending: res.data?.pending || null };
  } catch {
    return { pending: null };
  }
}

export async function dismissEvaluatorPending() {
  try {
    await apiFetch('/api/code-ide/evaluator/pending/dismiss', { method: 'POST' });
  } catch {
    /* swallow */
  }
}

/** Apply the full set of learned rules to .apolorules + commit by rule-id. */
export async function applyLearnedRules(root, rules, lastRuleId, { sessionId } = {}) {
  if (!root) return { ok: false, error: 'Sessão sem diretório (worktree)' };
  if (!hasRules() || !hasGit()) return { ok: false, error: 'Ponte Electron ausente (rode no app)' };
  const path = `${root}/.apolorules`;
  const applied = await window.apolo.apolorules.applyLearned({ path, rules });
  if (applied?.ok === false) return { ok: false, error: applied.error };
  if (applied?.changed === false) return { ok: true, changed: false };
  const commit = await window.apolo.git.commit({ root, message: `chore(learn): ${lastRuleId}` });
  if (commit?.ok === false) return { ok: false, error: commit.error || 'commit falhou' };
  if (sessionId) {
    const synced = await syncApoloRulesToSession(sessionId, root);
    if (!synced.ok) {
      return { ok: true, changed: true, syncWarning: synced.error || 'Regras salvas no git; sync da sessão falhou — reabra a sessão.' };
    }
  }
  return { ok: true, changed: true };
}

/**
 * Open a PR with the proposed learned rules instead of committing to the
 * checked-out branch. The autonomous loop uses THIS (not applyLearnedRules) so it
 * never writes straight to the branch — it proposes via a reviewable PR (isolated
 * worktree off origin/main, handled in electron/learnPR.cjs). Drop-in signature
 * with applyLearnedRules so evaluatorAutoRun can swap it as `applyFn`.
 *
 * @returns {{ ok, changed?, prUrl?, branch?, error? }}
 */
export async function openLearnedRulesPR(root, rules, lastRuleId, _opts = {}) {
  if (!root) return { ok: false, error: 'Sessão sem diretório (worktree)' };
  if (!hasGit() || typeof window.apolo?.git?.openLearnPR !== 'function') {
    return { ok: false, error: 'Ponte Electron ausente (rode no app)' };
  }
  const res = await window.apolo.git.openLearnPR({ root, rules, ruleId: lastRuleId });
  if (res?.ok === false) return { ok: false, error: res.detail || res.reason || 'PR do loop falhou' };
  // Mirror applyLearnedRules' shape so callers that check `.changed` keep working.
  return { ok: true, changed: res?.changed !== false, prUrl: res?.prUrl, branch: res?.branch };
}

/** Read + parse the learned rules currently in .apolorules. */
export async function listLearnedRules(root) {
  if (!root || !hasRules()) return [];
  const res = await window.apolo.apolorules.read({ path: `${root}/.apolorules` });
  if (!res?.ok) return [];
  return parseLearnedFromContent(res.content);
}

/** Revert a learned rule by reverting its `chore(learn): {rule-id}` commit. */
export async function revertLearnedRule(root, ruleId) {
  if (!root || !hasGit()) return { ok: false, error: 'Ponte Electron ausente (rode no app)' };
  const log = await window.apolo.git.logSearch({ root, grep: `chore(learn): ${ruleId}` });
  const sha = log?.commits?.[0]?.sha;
  if (!sha) return { ok: false, error: 'Commit da regra não encontrado' };
  const rev = await window.apolo.git.revert({ root, commit: sha });
  return rev?.ok ? { ok: true } : { ok: false, error: rev?.detail || rev?.error || 'revert falhou' };
}

export function useEvaluator(root, sessionId) {
  const [proposals, setProposals] = useState([]);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);

  const run = useCallback(async (scope = 'all') => {
    setRunning(true);
    setError(null);
    await ensureEvaluatorScheduleTimezone();
    const res = await runEvaluatorRun(scope);
    if (res.error) setError(res.error);
    else if (res.model_error) setError(formatSelfImproveError(res.model_error));
    setProposals(res.proposals);
    setRunning(false);
    return res;
  }, []);

  const loadPending = useCallback(async () => {
    const { pending } = await fetchEvaluatorPending();
    if (pending?.proposals?.length) {
      setProposals(pending.proposals);
      return pending;
    }
    return pending;
  }, []);

  const list = useCallback(() => listLearnedRules(root), [root]);
  const apply = useCallback(
    (rules, lastRuleId) => applyLearnedRules(root, rules, lastRuleId, { sessionId }),
    [root, sessionId],
  );
  const revert = useCallback((ruleId) => revertLearnedRule(root, ruleId), [root]);

  return { proposals, running, error, run, loadPending, dismissPending: dismissEvaluatorPending, list, apply, revert, setProposals };
}
